import * as THREE from 'three';
import { computeGridPositions, getGridPoint } from './grid';

// Renders a Grid into a Three.js scene as a set of thin gold lines (one
// LineSegments mesh for the inner cell boundaries, a slightly brighter
// one for the outer border), plus optional sphere handles at every
// intersection for the editor and a translucent overlay for the cell
// currently under the cursor.
//
// One renderer per scene. update(grid) is idempotent. Call it whenever
// the grid value changes. dispose() before unmounting the layer.
//
// The renderer never persists anything and never reads window/document.

const Y_LINES = 0.15;
const Y_HANDLES = 0.2;
const Y_HIGHLIGHT = 0.16;

const COLOR_LINE = 0xd4a843;
const COLOR_HANDLE_CORNER = 0xf0c050;
const COLOR_HANDLE_EDGE = 0xc0a040;
const COLOR_HANDLE_INTERIOR = 0x908060;
const COLOR_HIGHLIGHT = 0xf0c050;
const COLOR_HIGHLIGHT_FILL = 0xd4a843;

export class GridRenderer {
  constructor(scene) {
    this.scene = scene;
    this.linesMesh = null;
    this.borderMesh = null;
    this.handles = [];
    this.highlightMesh = null;
    this.highlightFill = null;
  }

  update(grid, { showHandles = true } = {}) {
    this.clearGridGeometry();
    if (!grid || !this.scene) return;

    const { colPositions, rowPositions } = computeGridPositions(grid);

    const innerVerts = [];
    for (let c = 1; c < colPositions.length - 1; c++) {
      const top = getGridPoint(grid, colPositions[c], 0);
      const bot = getGridPoint(grid, colPositions[c], 1);
      innerVerts.push(top.x, Y_LINES, top.z, bot.x, Y_LINES, bot.z);
    }
    for (let r = 1; r < rowPositions.length - 1; r++) {
      const left = getGridPoint(grid, 0, rowPositions[r]);
      const right = getGridPoint(grid, 1, rowPositions[r]);
      innerVerts.push(left.x, Y_LINES, left.z, right.x, Y_LINES, right.z);
    }
    if (innerVerts.length > 0) {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(innerVerts, 3));
      const mat = new THREE.LineBasicMaterial({ color: COLOR_LINE, transparent: true, opacity: 0.35 });
      this.linesMesh = new THREE.LineSegments(geo, mat);
      this.scene.scene.add(this.linesMesh);
    }

    const tl = getGridPoint(grid, 0, 0);
    const tr = getGridPoint(grid, 1, 0);
    const bl = getGridPoint(grid, 0, 1);
    const br = getGridPoint(grid, 1, 1);
    const borderVerts = [
      tl.x, Y_LINES, tl.z, tr.x, Y_LINES, tr.z,
      tr.x, Y_LINES, tr.z, br.x, Y_LINES, br.z,
      br.x, Y_LINES, br.z, bl.x, Y_LINES, bl.z,
      bl.x, Y_LINES, bl.z, tl.x, Y_LINES, tl.z,
    ];
    const borderGeo = new THREE.BufferGeometry();
    borderGeo.setAttribute('position', new THREE.Float32BufferAttribute(borderVerts, 3));
    const borderMat = new THREE.LineBasicMaterial({ color: COLOR_LINE, transparent: true, opacity: 0.7 });
    this.borderMesh = new THREE.LineSegments(borderGeo, borderMat);
    this.scene.scene.add(this.borderMesh);

    if (showHandles) this.createHandles(grid);
  }

  // Show a transient outline rectangle while the user is drag-selecting
  // a new grid bounds. Replaces any current grid geometry but leaves
  // the highlight overlay alone.
  previewRect(start, end) {
    this.clearGridGeometry();
    if (!start || !end || !this.scene) return;
    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minZ = Math.min(start.z, end.z);
    const maxZ = Math.max(start.z, end.z);
    const verts = [
      minX, Y_LINES, minZ, maxX, Y_LINES, minZ,
      maxX, Y_LINES, minZ, maxX, Y_LINES, maxZ,
      maxX, Y_LINES, maxZ, minX, Y_LINES, maxZ,
      minX, Y_LINES, maxZ, minX, Y_LINES, minZ,
    ];
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
    const mat = new THREE.LineBasicMaterial({ color: COLOR_LINE, transparent: true, opacity: 0.6 });
    this.borderMesh = new THREE.LineSegments(geo, mat);
    this.scene.scene.add(this.borderMesh);
  }

  highlightCell(cell) {
    if (!cell || !this.scene) { this.clearHighlight(); return; }
    const { tl, tr, bl, br } = cell;

    if (!this.highlightMesh) {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute([
        tl.x, Y_HIGHLIGHT, tl.z, tr.x, Y_HIGHLIGHT, tr.z,
        tr.x, Y_HIGHLIGHT, tr.z, br.x, Y_HIGHLIGHT, br.z,
        br.x, Y_HIGHLIGHT, br.z, bl.x, Y_HIGHLIGHT, bl.z,
        bl.x, Y_HIGHLIGHT, bl.z, tl.x, Y_HIGHLIGHT, tl.z,
      ], 3));
      const mat = new THREE.LineBasicMaterial({ color: COLOR_HIGHLIGHT, transparent: true, opacity: 0.8 });
      this.highlightMesh = new THREE.LineSegments(geo, mat);
      this.scene.scene.add(this.highlightMesh);

      const fillGeo = new THREE.BufferGeometry();
      fillGeo.setAttribute('position', new THREE.Float32BufferAttribute([
        tl.x, Y_HIGHLIGHT - 0.01, tl.z, tr.x, Y_HIGHLIGHT - 0.01, tr.z, bl.x, Y_HIGHLIGHT - 0.01, bl.z,
        tr.x, Y_HIGHLIGHT - 0.01, tr.z, br.x, Y_HIGHLIGHT - 0.01, br.z, bl.x, Y_HIGHLIGHT - 0.01, bl.z,
      ], 3));
      const fillMat = new THREE.MeshBasicMaterial({ color: COLOR_HIGHLIGHT_FILL, transparent: true, opacity: 0.12, side: THREE.DoubleSide });
      this.highlightFill = new THREE.Mesh(fillGeo, fillMat);
      this.scene.scene.add(this.highlightFill);
    } else {
      const pos = this.highlightMesh.geometry.attributes.position;
      pos.setXYZ(0, tl.x, Y_HIGHLIGHT, tl.z); pos.setXYZ(1, tr.x, Y_HIGHLIGHT, tr.z);
      pos.setXYZ(2, tr.x, Y_HIGHLIGHT, tr.z); pos.setXYZ(3, br.x, Y_HIGHLIGHT, br.z);
      pos.setXYZ(4, br.x, Y_HIGHLIGHT, br.z); pos.setXYZ(5, bl.x, Y_HIGHLIGHT, bl.z);
      pos.setXYZ(6, bl.x, Y_HIGHLIGHT, bl.z); pos.setXYZ(7, tl.x, Y_HIGHLIGHT, tl.z);
      pos.needsUpdate = true;

      const fPos = this.highlightFill.geometry.attributes.position;
      fPos.setXYZ(0, tl.x, Y_HIGHLIGHT - 0.01, tl.z); fPos.setXYZ(1, tr.x, Y_HIGHLIGHT - 0.01, tr.z); fPos.setXYZ(2, bl.x, Y_HIGHLIGHT - 0.01, bl.z);
      fPos.setXYZ(3, tr.x, Y_HIGHLIGHT - 0.01, tr.z); fPos.setXYZ(4, br.x, Y_HIGHLIGHT - 0.01, br.z); fPos.setXYZ(5, bl.x, Y_HIGHLIGHT - 0.01, bl.z);
      fPos.needsUpdate = true;
    }
    this.highlightMesh.visible = true;
    this.highlightFill.visible = true;
  }

  clearHighlight() {
    if (this.highlightMesh) this.highlightMesh.visible = false;
    if (this.highlightFill) this.highlightFill.visible = false;
  }

  // Meshes the caller can pass to a raycaster. Each handle carries
  // colIndex / rowIndex / isCorner / isEdge in userData, plus the
  // colPositions / rowPositions snapshot that applyHandleDrag needs.
  getHandleMeshes() {
    return this.handles;
  }

  createHandles(grid) {
    this.disposeHandles();
    const { colPositions, rowPositions } = computeGridPositions(grid);

    for (let r = 0; r < rowPositions.length; r++) {
      for (let c = 0; c < colPositions.length; c++) {
        const pt = getGridPoint(grid, colPositions[c], rowPositions[r]);
        const isCorner = (r === 0 || r === rowPositions.length - 1) && (c === 0 || c === colPositions.length - 1);
        const isEdge = !isCorner && (r === 0 || r === rowPositions.length - 1 || c === 0 || c === colPositions.length - 1);
        const color = isCorner ? COLOR_HANDLE_CORNER : isEdge ? COLOR_HANDLE_EDGE : COLOR_HANDLE_INTERIOR;
        const radius = isCorner ? 0.6 : 0.45;

        const geo = new THREE.SphereGeometry(radius, 12, 12);
        const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.85 });
        const mesh = new THREE.Mesh(geo, mat);
        mesh.position.set(pt.x, Y_HANDLES, pt.z);
        mesh.userData = {
          type: 'gridHandle',
          colIndex: c,
          rowIndex: r,
          isCorner,
          isEdge,
          colPositions,
          rowPositions,
        };
        this.scene.scene.add(mesh);
        this.handles.push(mesh);
      }
    }
  }

  clearGridGeometry() {
    if (this.linesMesh) {
      this.scene?.scene.remove(this.linesMesh);
      this.linesMesh.geometry.dispose();
      this.linesMesh.material.dispose();
      this.linesMesh = null;
    }
    if (this.borderMesh) {
      this.scene?.scene.remove(this.borderMesh);
      this.borderMesh.geometry.dispose();
      this.borderMesh.material.dispose();
      this.borderMesh = null;
    }
    this.disposeHandles();
  }

  disposeHandles() {
    for (const h of this.handles) {
      this.scene?.scene.remove(h);
      h.geometry.dispose();
      h.material.dispose();
    }
    this.handles = [];
  }

  dispose() {
    this.clearGridGeometry();
    if (this.highlightMesh) {
      this.scene?.scene.remove(this.highlightMesh);
      this.highlightMesh.geometry.dispose();
      this.highlightMesh.material.dispose();
      this.highlightMesh = null;
    }
    if (this.highlightFill) {
      this.scene?.scene.remove(this.highlightFill);
      this.highlightFill.geometry.dispose();
      this.highlightFill.material.dispose();
      this.highlightFill = null;
    }
  }
}
